import { isEmpty } from 'lodash';
import moment from 'moment';
import URI from 'urijs';
import { constants, windowWrapper } from '@clubmed/utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { IS_DISABLED, IS_ENABLED, IS_SELECTED } from 'Components/utils/product/product.utils';
import type { OfferDiscountProps } from 'Components/widgets/offer-discount/OfferDiscount';
import 'Components/widgets/offer-discount/OfferDiscount';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import 'Components/widgets/product/Product.utils';
import type { ProductPriceInfoProps } from 'Components/widgets/product-price-info/ProductPriceInfo';
import 'Components/widgets/product-price-info/ProductPriceInfo';
import { getAggregateDiscount } from 'Components/widgets/products-comparison/ProductsComparisonUtils';
import { t } from 'Src/i18n';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import { API_DATE_FORMAT, formatApiDate, momentFromApiDate } from 'Utils/date/apiDate';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import {
  QUERY_DATE_FORMAT,
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';
import { generateUrlWithParams, getQueryParameters } from 'Utils/uri/uriUtils';
import type { ProductComparisonProps } from './product-comparison/ProductComparison';
import type { ProductFlagProps } from './product-flag/ProductFlag';
import type { ProductHeaderProps } from './product-header/ProductHeader';
import type { Props as ProductThumbnailProps } from './product-thumbnail/ProductThumbnail';
type ProductPriceProps = ProductPrice & {
  style:
    | {
        backgroundColor: string;
      }
    | null
    | undefined;
  color: string;
  from?: string;
  instead?: string;
  flightIncluded?: string;
  viewRates?: string;
};
export type AvailabilityEnum = 'AVAILABLE' | 'UNAVAILABLE' | 'PARTIAL' | 'UNKNOWN';
export type ProductChildcareAvailability = {
  id: string;
  availability: AvailabilityEnum;
  ageMin: number;
  ageMax: number;
  isIncluded?: boolean;
  remainingStock: number | null;
  timeSlot: 'MORNING' | 'AFTERNOON' | '';
};
export type ProductChildcareAvailabilitiesProps = {
  items: Array<ProductChildcareAvailability>;
  monthsLabel: string;
  yearLabel: string;
  yearsLabel: string;
  remainingLabel: {
    n: string;
    _1: string;
  };
  availableLabel: string;
  unavailableLabel: string;
  unknownLabel: string;
  childcareLabel: string;
};
export type ChildcareAgeInMonthsProps =
  | Array<{
      ageInMonthsMin: number;
      ageInMonthsMax: number;
    }>
  | null
  | undefined;
export type ProductMappingProps = {
  comparison?: {
    isDisabled: boolean;
    isSelected: boolean;
  };
  config?: Record<string, any>;
  contextualizeUrl?: boolean;
  displayValidationButton?: boolean;
  hasComparison?: boolean;
  hasTransport?: boolean;
  isPriceLoading?: boolean;
  isSliderDisabled?: boolean;
  kids?: Array<Kids>;
  offer?: Offer;
  offerColor?: string;
  onClick?: () => any;
  product: ThumbnailProduct;
};
type ProductDiscountProps = {
  config: Record<string, any>;
  product: ProductToCompare;
};
const { OPENING_STATUS } = constants;
const DEFAULT_COMPARISON = {
  isDisabled: false,
  isSelected: false,
};

const getSubTitle = (product: ThumbnailProduct): string => {
  if (product.isCruise) {
    return product.timingDescription;
  }

  return product.destination?.country || '';
};

const getUrl = (href: string, termsAndConditions: TermsAndConditions, queryParams = {}) => {
  const departureDate = `${termsAndConditions?.departureDate || ''}`;
  const url = new URI(href).setQuery({
    locale: queryParams?.locale,
    departure_city: termsAndConditions?.departureCity?.value,
    duration: termsAndConditions?.duration,
    date:
      departureDate !== 'null' && departureDate
        ? formatApiDate(departureDate, QUERY_DATE_FORMAT)
        : undefined,
  });
  return url.toString();
};

const getProductUrl = (props: ProductMappingProps): string => {
  const isMice = !!props.product.mice;

  if (!props.contextualizeUrl) {
    return `${isMice ? '/me' : ''}${props.product.href}`;
  }

  const url = getUrl(props.product.href, props.product.price?.termsAndConditions || {});
  return `${isMice ? '/me' : ''}${url}`;
};

export const getProductUrlFromDestinationLine = (price: ProductPrice, queryParams: any): string =>
  getUrl(price.productHref || '', price.termsAndConditions, queryParams);

const getProductHeaderProps = (props: ProductMappingProps): ProductHeaderProps => {
  const { product } = props;
  const link = {
    url: getProductUrl(props),
  };
  const heading = {
    title: product.name,
    subtitle: getSubTitle(product),
  };
  return {
    heading,
    link,
  };
};

const getProductComparisonProps = ({
  comparison = DEFAULT_COMPARISON,
  config,
  product,
}: ProductMappingProps): ProductComparisonProps => {
  const { selected, selectResort, unavailableThumbnail } = config?.faceting?.compare || {};
  let status = IS_ENABLED;

  if (comparison.isDisabled) {
    status = IS_DISABLED;
  } else if (comparison.isSelected) {
    status = IS_SELECTED;
  }

  const label = comparison.isSelected ? selected : selectResort;
  const { isCruise, isTour } = product || {};
  return {
    status,
    label,
    isEligible: !isTour && !isCruise,
    eligibilityLabel: unavailableThumbnail,
  };
};

const getProductPriceProps = ({
  config,
  product,
  offer = {},
}: ProductMappingProps): ProductPriceProps => {
  const color = product.isJoyview ? COLORS.BLUE_WAVE : COLORS.SIENNA;
  return {
    style: {
      color: offer.color,
    },
    color: offer.color || color,
    ...(config?.price?.format || {}),
    ...(product.price || product.mice || {}),
  };
};

const getChildCareAgeInMonths = ({ product }: Props): ChildcareAgeInMonthsProps => {
  if (product.childcareDetails?.length) {
    return product.childcareDetails.map(({ ageInMonths }) => ({
      ageInMonthsMin: ageInMonths.min,
      ageInMonthsMax: ageInMonths.max,
    }));
  }
};

const getProductChildcareAvailabilitiesProps = ({
  config,
  product,
}: ProductMappingProps): ProductChildcareAvailabilitiesProps => {
  const {
    months: monthsLabel,
    years: yearsLabel,
    year: yearLabel,
    available: availableLabel,
    unknown: unknownLabel,
    unavailable: unavailableLabel,
    urgentizationSingular,
    urgentizationPlural,
    childcare: childcareLabel,
  } = config?.faceting?.availabilities || {};
  let sortedChildcareAvailabilities = (product.childcareAvailabilities || []).sort(
    (a, b) => a.ageMin - b.ageMin,
  );
  const [first = {}, second = {}]: Array<ProductChildcareAvailability> =
    sortedChildcareAvailabilities;
  const hasChildBetween4And23Months = first.ageMin === 4 || first.ageMax === 23;
  const hasOnlyBetween4And13Months = first.ageMin === 4 && second.ageMax !== 23;
  const hasOnlyBetween14And23Months = first.ageMax === 23;

  if (hasOnlyBetween4And13Months) {
    first.ageMax = 23;
  } else if (hasOnlyBetween14And23Months) {
    first.ageMin = 4;
  } else if (hasChildBetween4And23Months) {
    const stock =
      first.remainingStock === null && second.remainingStock === null
        ? null
        : (first.remainingStock || 0) + (second.remainingStock || 0);
    let availability = 'UNKNOWN';

    if (stock === 0) {
      availability = 'UNAVAILABLE';
    } else if (stock) {
      availability = stock <= 10 ? 'PARTIAL' : 'AVAILABLE';
    }

    sortedChildcareAvailabilities = [
      { ...second, remainingStock: stock, ageMin: 4, ageMax: 23, availability },
      ...sortedChildcareAvailabilities.slice(2),
    ];
  }

  return {
    items: sortedChildcareAvailabilities,
    monthsLabel,
    yearsLabel,
    yearLabel,
    availableLabel,
    unavailableLabel,
    unknownLabel,
    remainingLabel: {
      n: urgentizationPlural,
      _1: urgentizationSingular,
    },
    childcareLabel,
  };
};

const getProductPriceInfoProps = ({
  config,
  product,
}: ProductMappingProps): ProductPriceInfoProps => {
  let startDate = '';
  let endDate = '';
  const dateFormat =
    config?.resort?.startingPrice?.dateFormat || t('resort.startingPrice.dateFormat');
  startDate = moment(product?.price?.termsAndConditions?.departureDate, API_DATE_FORMAT).format(
    dateFormat,
  );
  endDate = moment(product?.price?.termsAndConditions?.departureDate, API_DATE_FORMAT)
    .add({
      days: product.price?.termsAndConditions?.duration || 0,
    })
    .format(dateFormat);
  return {
    city: product?.price?.termsAndConditions?.departureCity?.name || '',
    endDate,
    hasTransport: !!product?.price?.termsAndConditions?.departureCity?.value,
    isCruise: product.isCruise,
    isJoyview: product.isJoyview,
    isTour: product.isTour,
    rate: product?.price?.termsAndConditions?.offerId,
    startDate,
    transportIcon: product.transportType,
    unit: product.price?.unit || product.mice?.unit || '',
  };
};

const getTopTags = ({ hasTransport, product }) => {
  const { openingStatus, tags } = product;
  const hasDepartureCity = Boolean(product?.price?.termsAndConditions?.departureCity?.value);
  const isOpeningSoon = openingStatus === OPENING_STATUS.OPENING_SOON;
  const priorityTagslength = hasDepartureCity + isOpeningSoon + !!product.seasonTrad;
  const productTopTags = [
    ...(tags || []).slice(0, 3 - priorityTagslength).map((tag, i) => ({
      dataTestId: `ProductThumbnail-tag-${i}`,
      label: tag.title,
      style: {
        backgroundColor: tag.backgroundColor,
        borderColor: tag.backgroundColor,
        color: tag.textColor,
      },
    })),
  ];

  if (product.seasonTrad) {
    productTopTags.push({
      className: 'text-white border-turquoise bg-turquoise',
      dataTestId: 'ProductThumbnail-seasonBadge',
      label: product.seasonTrad,
    });
  }

  if (hasDepartureCity || hasTransport) {
    productTopTags.push({
      dataTestId: 'ProductThumbnail-transport',
      iconName: product.transportType,
      label: t('price.format.flightIncluded'),
    });
  }

  if (isOpeningSoon) {
    productTopTags.push({
      className: 'text-white border-orange bg-orange',
      dataTestId: 'ProductThumbnail-openingSoon',
      label: t('common.openingSoon'),
    });
  }

  return productTopTags;
};

const getProductTags = (product: Partial<ThumbnailProduct>) => {
  const productTags = [];

  if (product.mice) {
    const maxConferenceRoomCapacity = product.productMeetingRooms?.meetingRooms?.reduce(
      (roomMaxCapacity, { shapes }) => {
        return Math.max(
          roomMaxCapacity,
          shapes.reduce((maxShape, { maxCapacity }) => {
            return Math.max(maxCapacity, maxShape);
          }, 0),
        );
      },
      0,
    );

    if (maxConferenceRoomCapacity) {
      productTags.push({
        className: 'text-sienna bg-white border-white',
        label: t('common.conferenceRoomCapacity', {
          maxConferenceRoomCapacity,
        }),
      });
    }

    if (product.accommodationsIntroduction?.roomsCount) {
      productTags.push({
        className: 'text-sienna border-white bg-white',
        label: t('common.numberOfRooms', {
          numberOfRooms: product.accommodationsIntroduction.roomsCount,
        }),
      });
    }

    return productTags;
  }

  if (product.isTour) {
    const rangeLabel = product.rangeLabel?.toUpperCase();
    return [
      {
        className: 'text-white border-sienna bg-sienna',
        label: product.duration ? `${rangeLabel} : ${product.duration}` : rangeLabel,
      },
    ];
  }

  if (product.isCruise) {
    const label = [product.thematics?.[0]?.label, product.duration].filter(Boolean).join(' - ');
    return [
      {
        className: 'text-white border-ultramarine bg-ultramarine',
        label,
      },
    ];
  }

  if (product.hasECSpace) {
    return [
      {
        className: 'text-ultramarine border-white bg-white',
        iconName: ICONS.DIAMOND,
        label: t('common.product.exclusiveCollectionSpaceTag'),
      },
    ];
  }

  if (product.isExplicitEC) {
    return [
      {
        className: 'text-ultramarine border-white bg-white',
        iconName: ICONS.DIAMOND,
        label: t('common.product.exclusiveCollectionTag'),
      },
    ];
  }
};

const getProductThumbnailProps = (props: ProductMappingProps): ProductThumbnailProps => {
  const { product, isSliderDisabled = false } = props;
  const { openingStatus } = product;
  return {
    bottomTags: getProductTags(product),
    closedLabel: t('common.closed'),
    isSliderDisabled,
    link: {
      url: getProductUrl(props),
    },
    openingStatus,
    pictures: (product.images || []).map((image) => ({
      alt: product.name,
      src: image || '',
    })),
    topTags: getTopTags(props),
  };
};

const getProductFlagProps = (props: ProductMappingProps): ProductFlagProps => {
  const { isPriceLoading = false, product } = props;
  const { openingStatus = OPENING_STATUS.OPEN } = product;
  const price = openingStatus === OPENING_STATUS.OPEN ? getProductPriceProps(props) : {};
  return {
    price,
    priceInfo: getProductPriceInfoProps(props),
    isPriceLoading,
  };
};

const getProductOfferDiscountProps = ({
  product,
  config,
}: ProductDiscountProps): OfferDiscountProps => ({
  discount: getAggregateDiscount(product),
  i18n: config.offerDiscount,
});

const createProductUrlWithParams = (
  href: string,
  product: Partial<ThumbnailProduct> | ProductProps,
) => {
  const currentLocation = windowWrapper.getLocation();
  let possibleQueries = [QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_TRANSPORT, QUERY_KIDS];

  if (product.isCruise) {
    possibleQueries = [QUERY_TRANSPORT];
  } else if (product.isTour) {
    possibleQueries = [QUERY_DEPARTURE_DATE, QUERY_TRANSPORT];
  }

  const getDepartureDate = () => {
    const departureDate = product?.flag?.price?.termsAndConditions?.departureDate;
    if (product.departureDateOn && departureDate) {
      return momentFromApiDate(departureDate).format(QUERY_DATE_FORMAT);
    }

    return getQueryParameters(currentLocation)[QUERY_DEPARTURE_DATE] || '';
  };

  return generateUrlWithParams(href, {
    queryParams: possibleQueries.reduce(
      (acc, curr) => ({
        ...acc,
        [curr]:
          curr === QUERY_DEPARTURE_DATE
            ? getDepartureDate()
            : getQueryParameters(currentLocation)[curr] || '',
      }),
      {},
    ),
  });
};

const setProductHref = (props: ProductMappingProps): ProductMappingProps => {
  const { product = {} } = props;
  const { href } = product;

  if (href && typeof window !== 'undefined') {
    return {
      ...props,
      product: {
        ...product,
        href: createProductUrlWithParams(href, product),
      } as ProductMappingProps['product'],
    };
  }

  return props;
};

const getProductProps = (props: ProductMappingProps): ProductProps => {
  const { hasComparison = false, kids = [], offer = {}, product = {} } = props;
  const flag = getProductFlagProps(props);
  const flagPrice = flag.price || {};
  const matchingOfferProducts =
    offer.products && offer.products.length > 0
      ? offer.products.filter((offerProduct) => offerProduct.id === product.id)
      : [];
  const price =
    matchingOfferProducts.length === 1
      ? matchingOfferProducts.map((matchingOfferProduct) => matchingOfferProduct.price).shift()
      : flagPrice;
  return {
    childcare: getProductChildcareAvailabilitiesProps(props),
    childcareAgeInMonths: getChildCareAgeInMonths(props),
    childcareIncluded: props.product?.childcareDetails?.some((childcare) => childcare.included),
    comfort: product.comfort,
    comparison: getProductComparisonProps(props),
    coordinates: product.coordinates,
    departureDateOn: product.departureDateOn,
    flag,
    hasComparison,
    header: getProductHeaderProps(props),
    id: product.id,
    isCruise: product.isCruise,
    isTour: product.isTour,
    kids,
    minAuthorizedAge: props.product.minAuthorizedAge,
    price: !isEmpty(price) ? price : undefined,
    resortRegion: product.destination?.area,
    thumbnail: getProductThumbnailProps(props),
    type: product.type,
    url: getProductUrl(props),
  };
};

const updateProduct = (product: ProductProps, props: ProductMappingProps): ProductProps => ({
  ...product,
  hasComparison: props.hasComparison,
  childcare: getProductChildcareAvailabilitiesProps(props),
  comparison: getProductComparisonProps(props),
  flag: getProductFlagProps(props),
});

const updateProductUrl = (product: ProductProps): ProductProps => {
  const newProductUrl = createProductUrlWithParams(new URI(product.url).pathname(), product);
  return {
    ...product,
    url: newProductUrl,
    header: {
      ...product.header,
      link: {
        url: newProductUrl,
      },
    },
    thumbnail: {
      ...product.thumbnail,
      link: {
        url: newProductUrl,
      },
    },
  };
};

export {
  getProductProps,
  getProductTags,
  getProductOfferDiscountProps,
  getProductChildcareAvailabilitiesProps,
  getProductComparisonProps,
  getProductFlagProps,
  getProductHeaderProps,
  getProductPriceProps,
  getProductPriceInfoProps,
  getProductThumbnailProps,
  setProductHref,
  updateProductUrl,
  createProductUrlWithParams,
  updateProduct,
};
